import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { getFallEvents, updateFallEvent } from '../../services/fallEventService';

export const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'FALL_RECORDED', label: 'Fall Recorded' },
  { value: 'CLIENT_LABELED', label: 'Client Labeled' },
  { value: 'ADMIN_REVIEWED', label: 'Admin Reviewed' },
  { value: 'ESCALATED', label: 'Escalated' },
  { value: 'CLOSED', label: 'Closed' },
];

export const PERIOD_OPTIONS = [
  { value: '1', label: 'Today' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
];

const ALL_CAMERAS_OPTION = { value: 'all', label: 'All cameras' };

const useFallEvents = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = (searchParams.get('status') || 'ALL').toUpperCase();
  const initialPeriod = searchParams.get('period') || '30';
  const initialCamera = searchParams.get('camera') || ALL_CAMERAS_OPTION.value;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [periodFilter, setPeriodFilter] = useState(initialPeriod);
  const [cameraFilter, setCameraFilter] = useState(initialCamera);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getFallEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Unable to load fall events.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    const params = {};
    if (statusFilter && statusFilter !== 'ALL') {
      params.status = statusFilter;
    }
    if (periodFilter && periodFilter !== '30') {
      params.period = periodFilter;
    }
    if (cameraFilter && cameraFilter !== ALL_CAMERAS_OPTION.value) {
      params.camera = cameraFilter;
    }
    setSearchParams(params, { replace: true });
  }, [cameraFilter, periodFilter, setSearchParams, statusFilter]);

  const cameraOptions = useMemo(() => {
    const unique = new Map();
    events.forEach((event) => {
      const rawId = event.camera?.id ?? event.camera_id ?? event.camera_name ?? event.camera?.name;
      if (!rawId || unique.has(rawId)) return;
      const label = event.camera?.name
        || event.camera_name
        || (event.camera_id ? `Camera ${event.camera_id}` : 'Unknown camera');
      unique.set(rawId, { value: String(rawId), label });
    });
    return [ALL_CAMERAS_OPTION, ...Array.from(unique.values())];
  }, [events]);

  const filteredEvents = useMemo(() => {
    const selectedStatus = statusFilter === 'ALL' ? null : statusFilter;
    const periodValue = periodFilter === 'all' ? null : Number(periodFilter);
    const now = new Date();

    return events.filter((event) => {
      const statusMatches = selectedStatus ? event.status?.toUpperCase() === selectedStatus : true;
      if (!statusMatches) {
        return false;
      }
      const cameraMatches = cameraFilter === ALL_CAMERAS_OPTION.value
        ? true
        : String(event.camera?.id ?? event.camera_id ?? event.camera?.name) === cameraFilter;
      if (!cameraMatches) {
        return false;
      }
      if (!periodValue || Number.isNaN(periodValue) || periodValue <= 0) {
        return true;
      }
      const eventDate = new Date(event.timestamp || event.detected_at || event.created_at || 0);
      const diffMs = now - eventDate;
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      return diffDays < periodValue;
    });
  }, [cameraFilter, events, periodFilter, statusFilter]);

  const handleUpdate = async (eventId, payload) => {
    setUpdating(true);
    setError('');
    try {
      const updatedEvent = await updateFallEvent(eventId, payload);
      if (updatedEvent?.id) {
        setEvents((prev) => prev.map((event) => (event.id === eventId ? updatedEvent : event)));
      }
      return updatedEvent;
    } catch (err) {
      setError('Unable to update fall event.');
      throw err;
    } finally {
      setUpdating(false);
    }
  };

  return {
    events: filteredEvents,
    rawEvents: events,
    loading,
    error,
    updating,
    statusFilter,
    periodFilter,
    cameraFilter,
    setStatusFilter,
    setPeriodFilter,
    setCameraFilter,
    handleUpdate,
    setEvents,
    setError,
    setUpdating,
    loadEvents,
    cameraOptions,
  };
};

export default useFallEvents;

